import { ArrowRight, Boxes, Compass, Database, ExternalLink, Package, Workflow } from 'lucide-react';
import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';
import { StatusBadge } from '../components/StatusBadge';
import { useCatalog } from '../useCatalog';
import {
  EXPLORER_URL,
  TOOLS_URL,
  buildingCapabilities,
  exploringCapabilities,
  liveCapabilities,
  resolveCapabilityNote,
  type Capability,
} from '../config';

const palette = {
  heroDark: '#4A97AC',
  heroMid: '#97BFE6',
  heroLight: '#D4E2F4',
  border: '#D4E2F4',
  surface: '#F4F7FC',
  accent: '#0F6B7C',
};

const groups = [
  {
    id: 'g1',
    tag: 'G1 · Data',
    title: 'Data',
    icon: Database,
    description:
      'The raw datasets: ODiN travel surveys, VK500 grids, CBS boundaries, road and transit networks. For those who build analyses.',
  },
  {
    id: 'g2',
    tag: 'G2 · Tools',
    title: 'Tools',
    icon: Boxes,
    description:
      'Self-service tools at tools.sb.nl. Answer common questions yourself — no data-team ticket required.',
  },
  {
    id: 'g3',
    tag: 'G3 · Products',
    title: 'Products',
    icon: Package,
    description:
      'Finished deliverables: styled maps, dashboards, and reports ready for clients.',
  },
];

function CapabilityColumn({
  status,
  items,
  processCount,
  domainCount,
}: {
  status: 'live' | 'building' | 'exploring';
  items: Capability[];
  processCount: number | null;
  domainCount: number | null;
}) {
  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: palette.border, backgroundColor: palette.surface }}>
      <StatusBadge status={status} />
      <ul className="mt-4 space-y-3">
        {items.map((item) => {
          const note = resolveCapabilityNote(item, { processCount, domainCount });
          return (
            <li key={item.label}>
              <p className="text-sm font-semibold text-sb-black">{item.label}</p>
              {note && <p className="mt-0.5 text-xs text-sb-dark-grey">{note}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function OverviewPage() {
  const { processCount, domainCount } = useCatalog();

  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="Overview" />

      <PagePurpose
        title="One platform, from data to decisions"
        audience="Everyone at Studio Bereikbaar — consultants, project managers, and the data team"
        description="How our data, methods, and tools connect in one place. Start here to understand the idea behind the platform and where each piece fits."
        badge="Start here"
      />

      {/* Hero */}
      <section
        className="rounded-3xl text-white shadow-xl"
        style={{
          background: `linear-gradient(135deg, ${palette.heroDark} 0%, ${palette.heroMid} 60%, ${palette.heroLight} 100%)`,
        }}
      >
        <div className="p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/80">
            How the platform works
          </p>
          <h1 className="mt-4 text-4xl font-bold leading-tight">One platform, from data to decisions</h1>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-white/90">
            Our data, methods, and tools connected in one place — so a mobility question becomes an
            answer in minutes, not days.
          </p>
        </div>
      </section>

      {/* Section A — Who it's for */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Who it's for</p>
          <h2 className="text-3xl font-semibold text-sb-black">Three groups, one platform</h2>
          <p className="text-sm text-sb-dark-grey">
            Whatever your role, there's a way in — from raw data to finished deliverables.
          </p>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {groups.map((group) => (
            <div
              key={group.id}
              className="rounded-2xl border p-6"
              style={{ borderColor: palette.border, backgroundColor: '#FFFFFF' }}
            >
              <div className="flex items-center gap-3">
                <group.icon size={22} className="text-[#4A97AC]" aria-hidden />
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.tag}</p>
              </div>
              <h3 className="mt-3 text-xl font-semibold text-sb-black">{group.title}</h3>
              <p className="mt-2 text-sm text-sb-dark-grey">{group.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section B — How it's built */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <div className="flex items-start gap-3">
          <Workflow size={24} className="mt-1 flex-shrink-0 text-[#4A97AC]" aria-hidden />
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">The idea</p>
            <h2 className="text-3xl font-semibold text-sb-black">Write a method once, use it everywhere</h2>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-base text-sb-dark-grey">
          When Roland or Susan defines a methodology, Giovanni encodes it once as an API endpoint — and
          it becomes available as a self-service tool, inside QGIS, to Claude, and testable in the live API
          explorer, with no extra integration per surface. That keeps our innovation cycle short.
        </p>
        <div
          className="mt-5 rounded-2xl border p-5"
          style={{ borderColor: palette.border, backgroundColor: palette.surface }}
        >
          <p className="text-sm text-sb-dark-grey">
            This isn't a promise — the API explorer below lists every method that's live right now,
            straight from the registry:{' '}
            {processCount === null ? (
              <span className="font-semibold text-sb-black">live figures temporarily unavailable</span>
            ) : (
              <span className="font-semibold text-sb-black">
                {processCount} method{processCount === 1 ? '' : 's'} live today
              </span>
            )}
            .
          </p>
        </div>
      </section>

      {/* Section C — The backbone */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">The backbone</p>
          <StatusBadge status="live" />
        </div>
        <h2 className="mt-2 text-3xl font-semibold text-sb-black">One registry, one source of truth</h2>
        <p className="mt-4 max-w-3xl text-base text-sb-dark-grey">
          One source of truth for where every dataset physically lives. The tools, QGIS, MapHub, and
          Claude all ask the registry — so they never disagree, and these docs read their numbers from it too.
        </p>
      </section>

      {/* Section D — Honest status legend + capability grid */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Honest status</p>
          <h2 className="text-3xl font-semibold text-sb-black">What's live, building, and exploring</h2>
          <p className="text-sm text-sb-dark-grey">
            We label every capability plainly so you always know what you can rely on today.
          </p>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border p-4" style={{ borderColor: palette.border }}>
            <StatusBadge status="live" />
            <p className="mt-2 text-xs text-sb-dark-grey">In production and safe to use right now.</p>
          </div>
          <div className="rounded-2xl border p-4" style={{ borderColor: palette.border }}>
            <StatusBadge status="building" />
            <p className="mt-2 text-xs text-sb-dark-grey">Being worked on — not finished, don't rely on it yet.</p>
          </div>
          <div className="rounded-2xl border p-4" style={{ borderColor: palette.border }}>
            <StatusBadge status="exploring" />
            <p className="mt-2 text-xs text-sb-dark-grey">An idea we're weighing, not committed to.</p>
          </div>
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <CapabilityColumn status="live" items={liveCapabilities} processCount={processCount} domainCount={domainCount} />
          <CapabilityColumn status="building" items={buildingCapabilities} processCount={processCount} domainCount={domainCount} />
          <CapabilityColumn status="exploring" items={exploringCapabilities} processCount={processCount} domainCount={domainCount} />
        </div>
      </section>

      {/* Try the data live — prominent launchpad */}
      <section
        className="rounded-3xl border p-8 shadow-sm"
        style={{ borderColor: palette.accent, backgroundColor: palette.surface }}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.accent }}>
              Try it now
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-sb-black">Try the data live</h2>
            <p className="mt-2 max-w-2xl text-sm text-sb-dark-grey">
              Open the live API explorer and run any method against real data — no setup, straight from the registry.
            </p>
          </div>
          <a
            href={EXPLORER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: palette.accent }}
          >
            Try the data live <ArrowRight size={18} aria-hidden />
          </a>
        </div>
      </section>

      {/* Section E — Launchpad cards */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Where to go next</p>
          <h2 className="text-3xl font-semibold text-sb-black">Launchpad</h2>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <a
            href={EXPLORER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border p-5 transition hover:border-[#A6C7E9] hover:shadow-sm"
            style={{ borderColor: palette.border }}
          >
            <ExternalLink size={20} className="text-[#4A97AC]" aria-hidden />
            <h3 className="mt-3 text-lg font-semibold text-sb-black">Explore the data live</h3>
            <p className="mt-2 text-sm text-sb-dark-grey">The live API explorer, straight from the registry.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: palette.accent }}>
              Open <ArrowRight size={16} aria-hidden />
            </span>
          </a>
          <a
            href={TOOLS_URL}
            className="rounded-2xl border p-5 transition hover:border-[#A6C7E9] hover:shadow-sm"
            style={{ borderColor: palette.border }}
          >
            <Boxes size={20} className="text-[#4A97AC]" aria-hidden />
            <h3 className="mt-3 text-lg font-semibold text-sb-black">Browse the tools</h3>
            <p className="mt-2 text-sm text-sb-dark-grey">Self-service tools you can run yourself.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: palette.accent }}>
              Open <ArrowRight size={16} aria-hidden />
            </span>
          </a>
          <a
            href="/documentation/methodologies"
            className="rounded-2xl border p-5 transition hover:border-[#A6C7E9] hover:shadow-sm"
            style={{ borderColor: palette.border }}
          >
            <Compass size={20} className="text-[#4A97AC]" aria-hidden />
            <h3 className="mt-3 text-lg font-semibold text-sb-black">The methods</h3>
            <p className="mt-2 text-sm text-sb-dark-grey">Every analysis method, live from the registry.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: palette.accent }}>
              Open <ArrowRight size={16} aria-hidden />
            </span>
          </a>
        </div>
      </section>

      <DocPagination currentPageId="overview" />
    </div>
  );
}
